import { useRouter } from 'next/router';
import { useMemo, useCallback, useEffect } from 'react';
import { useMedia } from 'react-use';
import type { BreadcrumbItem } from '~/common/components/bread-crumb-list';
import { useStoredFollowingCount } from '~/common/hooks/use-auth/use-stored-following-count';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import { useFirebaseAnalytics } from '~/common/hooks/use-firebase-analytics';
import { useFollowListings } from '~/common/hooks/use-follow-listings/use-follow-listings';
import { useUserAgent } from '~/common/hooks/use-user-agent';
import { NOVEL_STUDIO_URL } from '~/utils/constants';
import { routes } from '~/utils/routes';
import { Media } from '~/utils/styles/enums';
import { useSNSProviders } from './use-sns-providers';
import { useSubscriptionInfo } from './use-subscription-info';

export const usePage = () => {
  const { session } = useTellerSession();
  const screenLarge = useMedia(Media.LARGE);
  const router = useRouter();
  const { logEvent } = useFirebaseAnalytics();

  const { isMobile } = useUserAgent();

  const { SNSProviders, hasEmailProvider, hasPhoneProvider } = useSNSProviders(
    session?.providerData
  );
  const { hasPaymentInformation } = useSubscriptionInfo(session?.tellerUser);

  const pageMetaData = useMemo(() => {
    return {
      title: routes.myPage.title(),
      description: routes.myPage.title(),
      url: routes.myPage.url(),
    };
  }, []);

  const breadcrumbList: BreadcrumbItem[] = useMemo(() => {
    return [
      {
        name: routes.myPage.breadCrumbTitle(),
        path: routes.myPage.url(),
      },
    ];
  }, []);

  useEffect(() => {
    if (session && session?.isAnonymous) {
      router.push(routes.home.url());
    }
  }, [router, session]);

  const officialBadge = useMemo(() => {
    return session?.tellerUser?.isOfficialWriter || false;
  }, [session?.tellerUser?.isOfficialWriter]);

  const vipBadge = useMemo(() => {
    return (!officialBadge && session?.tellerUser?.isVipUser) || false;
  }, [officialBadge, session?.tellerUser?.isVipUser]);

  const toNovelStudio = useCallback(
    async (op?: string) => {
      if (op === 'write') {
        logEvent('mypage_click_write_story', {
          email: session?.email,
        });
      } else {
        logEvent('mypage_click_my_story', {
          email: session?.email,
        });
      }
      window.location.href = `${NOVEL_STUDIO_URL}?webUid=${session?.uid}`;
    },
    [logEvent, session?.email, session?.uid]
  );

  const { followingCount } = useStoredFollowingCount();

  const checkingSession = useMemo(() => {
    return !session;
  }, [session]);

  const {
    followListModalType,
    isFollowListingsModalOpen,
    closeFollowListingsModal,
    handleTabClick,
    handleUserFollowStatsClick,
  } = useFollowListings();

  return {
    screenLarge,
    isMobile,
    pageMetaData,
    breadcrumbList,
    tellerUser: session?.tellerUser,
    officialBadge,
    vipBadge,
    toNovelStudio,
    SNSProviders,
    hasEmailProvider,
    hasPhoneProvider,
    hasPaymentInformation,
    followingCount: followingCount(),
    followListModalType,
    isFollowListingsModalOpen,
    closeFollowListingsModal,
    handleTabClick,
    handleUserFollowStatsClick,
    checkingSession,
  };
};
